import { useMutation } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type {
    CreateContainerRequest,
    CreateContainerResponse,
} from '@xver/apis/v1/kufer/containers/model';

interface UseCreateContainerMutationOptions {
    onSuccess?: (data: CreateContainerResponse) => void;
    onError?: (error: unknown) => void;
}

/**
 * UTF-8 safe base64 encoder for browsers. The kufer API expects the inline
 * dockerfile content to be base64 encoded before being sent.
 */
function encodeBase64Utf8(value: string): string {
    return btoa(unescape(encodeURIComponent(value)));
}

export function useCreateContainerMutation(options: UseCreateContainerMutationOptions = {}) {
    const { onSuccess, onError } = options;

    return useMutation<CreateContainerResponse, unknown, CreateContainerRequest>({
        mutationFn: async (req: CreateContainerRequest) => {
            const payload: CreateContainerRequest = {
                ...req,
                source: { ...req.source },
            };

            if (
                payload.source.type === 'inline' &&
                typeof payload.source.inline === 'string' &&
                payload.source.inline.length > 0
            ) {
                payload.source.inline = encodeBase64Utf8(payload.source.inline);
            }

            return Endpoints.v1.Kufer.Containers.createContainer(payload);
        },
        onSuccess,
        onError,
    });
}
